import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Radio,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Paper,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Part } from '../../../../services/api/partsService';
import './ReplacePartModal.scss';

export interface SelectedReplacementPart {
  partId: string;
  name: string;
  mark?: string;
  globalId?: string;
  weight?: number | null;
  type?: string;
  isAlreadyPlaced?: boolean;
  orderNumber?: number;
  layer?: number;
}

export interface ReplacePartModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (replacement: SelectedReplacementPart) => void;
  currentPartId: string | null;
  currentPartName?: string;
  currentPartOrderNumber?: number;
  currentStepNumber?: number;
  nestedParts?: Array<{
    part_id: string;
    assigned_loading_layer?: number;
    order_number?: number;
    transform_matrix?: number[][];
  }>;
  includedParts?: Part[];
  excludedParts?: Array<{
    part_id: string;
    reason?: string;
  }>;
}

export const ReplacePartModal: React.FC<ReplacePartModalProps> = ({
  open,
  onClose,
  onConfirm,
  currentPartId,
  currentPartName,
  currentPartOrderNumber = 0,
  currentStepNumber,
  nestedParts = [],
  includedParts = [],
  excludedParts = [],
}) => {
  const [activeTab, setActiveTab] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedPartId, setSelectedPartId] = useState<string | null>(null);

  // Helper map: GlobalId or PartId -> Part metadata from DB
  const includedPartsMap = useMemo(() => {
    const map = new Map<string, Part>();
    includedParts.forEach((part) => {
      map.set(part.globalId, part);
      map.set(`${part.sanitizedName}_${part.globalId}`, part);
      map.set(`${part.sanitizedName}_${part.mark}`, part);
    });
    return map;
  }, [includedParts]);

  // Excluded parts reason map
  const excludedReasonsMap = useMemo(() => {
    const map = new Map<string, string>();
    excludedParts.forEach((item) => {
      if (item.part_id && item.reason) {
        map.set(item.part_id, item.reason);
      }
    });
    return map;
  }, [excludedParts]);

  // Calculate sequential viewer step numbers (1-based) for all placed non-dunnage parts
  const placedPartsStepMap = useMemo(() => {
    const map = new Map<string, number>();

    const nonDunnageParts = [...nestedParts]
      .filter((p) => {
        const baseId = p.part_id.includes('#') ? p.part_id.split('#')[0] : p.part_id;
        return baseId !== 'dunnage';
      })
      .sort((a, b) => {
        const layerA = a.assigned_loading_layer ?? 1;
        const layerB = b.assigned_loading_layer ?? 1;
        if (layerA !== layerB) return layerA - layerB;
        return (a.order_number ?? 0) - (b.order_number ?? 0);
      });

    nonDunnageParts.forEach((part, index) => {
      map.set(part.part_id, index + 1);
    });

    return map;
  }, [nestedParts]);

  // 1. Placed parts that come AFTER the current part in the viewer step sequence
  const placedLaterParts = useMemo(() => {
    const currentStep = currentStepNumber ?? (currentPartId ? placedPartsStepMap.get(currentPartId) : undefined) ?? 0;

    return [...nestedParts]
      .filter((p) => {
        const baseId = p.part_id.includes('#') ? p.part_id.split('#')[0] : p.part_id;
        const isSupport = baseId === 'dunnage' || baseId === 'pallet';
        const isSelf = p.part_id === currentPartId;
        const stepNum = placedPartsStepMap.get(p.part_id) ?? 0;
        const isAfter = currentStep > 0 ? stepNum > currentStep : (p.order_number ?? 0) > currentPartOrderNumber;
        return !isSupport && !isSelf && isAfter;
      })
      .sort((a, b) => {
        const stepA = placedPartsStepMap.get(a.part_id) ?? (a.order_number ?? 0);
        const stepB = placedPartsStepMap.get(b.part_id) ?? (b.order_number ?? 0);
        return stepA - stepB;
      })
      .map((p) => {
        const meta = includedPartsMap.get(p.part_id);
        const name = meta ? meta.name : p.part_id;
        const mark = meta ? meta.mark : '-';
        const type = meta ? meta.type : '-';
        const weight = meta ? meta.weight : null;
        const stepNumber = placedPartsStepMap.get(p.part_id) ?? p.order_number;
        return {
          partId: p.part_id,
          name,
          mark,
          type,
          weight,
          orderNumber: stepNumber,
          layer: p.assigned_loading_layer,
          isAlreadyPlaced: true,
          globalId: meta?.globalId,
        };
      });
  }, [nestedParts, currentPartId, currentStepNumber, currentPartOrderNumber, placedPartsStepMap, includedPartsMap]);

  // 2. Unplaced / Excluded parts
  const unplacedParts = useMemo(() => {
    const placedPartIds = new Set<string>();
    nestedParts.forEach((p: any) => {
      if (p.part_id) {
        placedPartIds.add(p.part_id);
        const baseId = p.part_id.includes('#') ? p.part_id.split('#')[0] : p.part_id;
        placedPartIds.add(baseId);
      }
      if (p.child_items && Array.isArray(p.child_items)) {
        p.child_items.forEach((child: any) => {
          if (child.part_id) {
            placedPartIds.add(child.part_id);
            const baseId = child.part_id.includes('#') ? child.part_id.split('#')[0] : child.part_id;
            placedPartIds.add(baseId);
          }
        });
      }
    });

    return includedParts
      .filter((part) => {
        const fullIdWithGlobal = `${part.sanitizedName}_${part.globalId}`;
        const fullIdWithMark = `${part.sanitizedName}_${part.mark}`;
        const isPlaced =
          placedPartIds.has(fullIdWithGlobal) ||
          placedPartIds.has(fullIdWithMark) ||
          placedPartIds.has(part.globalId) ||
          placedPartIds.has(part.mark) ||
          placedPartIds.has(part.name) ||
          placedPartIds.has(String(part.id));
        return !isPlaced;
      })
      .map((part) => {
        const fullId = `${part.sanitizedName}_${part.globalId}`;
        const reason =
          excludedReasonsMap.get(fullId) ||
          excludedReasonsMap.get(part.globalId) ||
          excludedReasonsMap.get(`${part.sanitizedName}_${part.mark}`) ||
          excludedReasonsMap.get(part.mark);
        return {
          partId: fullId,
          name: part.name,
          mark: part.mark,
          type: part.type,
          weight: part.weight,
          reason,
          isAlreadyPlaced: false,
          globalId: part.globalId,
        };
      });
  }, [includedParts, nestedParts, excludedReasonsMap]);

  // Filter based on search query
  const filteredPlacedParts = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) return placedLaterParts;
    return placedLaterParts.filter(
      (p) =>
        p.name.toLowerCase().includes(query) ||
        p.mark.toLowerCase().includes(query) ||
        p.type.toLowerCase().includes(query)
    );
  }, [placedLaterParts, searchQuery]);

  const filteredUnplacedParts = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) return unplacedParts;
    return unplacedParts.filter(
      (p) =>
        p.name.toLowerCase().includes(query) ||
        p.mark.toLowerCase().includes(query) ||
        p.type.toLowerCase().includes(query)
    );
  }, [unplacedParts, searchQuery]);

  // Nothing to swap this part with (e.g. it's the last part in the sequence and no parts are unplaced).
  const hasReplacementOptions = placedLaterParts.length > 0 || unplacedParts.length > 0;

  // Reset selection when modal opens
  useEffect(() => {
    if (open) {
      setSelectedPartId(null);
      setSearchQuery('');
      setActiveTab(placedLaterParts.length > 0 ? 0 : 1);
    }
  }, [open, placedLaterParts.length]);

  const handleConfirm = () => {
    if (!selectedPartId) return;

    const allOptions = [...placedLaterParts, ...unplacedParts];
    const selected = allOptions.find((p) => p.partId === selectedPartId);
    if (selected) {
      onConfirm(selected);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} className="replace-part-dialog" maxWidth="md" fullWidth>
      <DialogTitle className="replace-part-header">
        <Box display="flex" alignItems="center" gap={1}>
          <SwapHorizIcon color="primary" />
          <Typography variant="h6" component="span">
            Replace Part: <strong>{currentPartName || currentPartId}</strong>
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className="replace-part-content">
        {!hasReplacementOptions ? (
          <Box
            className="replace-part-empty-state"
            sx={{
              py: 6,
              px: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1.5 }} />
            <Typography variant="h6" color="text.primary" gutterBottom>
              No parts available to replace
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
              There are no subsequent placed parts or unplaced parts available to replace this part with.
            </Typography>
          </Box>
        ) : (
          <>
        <Tabs
          value={activeTab}
          onChange={(_, newTab) => {
            setActiveTab(newTab);
            setSelectedPartId(null);
          }}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <span>Placed Parts (Later)</span>
                <Chip label={placedLaterParts.length} size="small" />
              </Box>
            }
          />
          <Tab
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <span>Unplaced Parts</span>
                <Chip label={unplacedParts.length} size="small" />
              </Box>
            }
          />
        </Tabs>

        <TextField
          className="replace-part-search"
          size="small"
          placeholder="Search by part name, mark, or type..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />

        {activeTab === 0 ? (
          <TableContainer component={Paper} className="replace-part-table-container" elevation={0}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>Step #</TableCell>
                  <TableCell>Layer</TableCell>
                  <TableCell>Mark</TableCell>
                  <TableCell>Part Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Weight (lbs)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPlacedParts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                      No subsequent placed steel parts available to swap.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredPlacedParts.map((part) => (
                    <TableRow
                      key={part.partId}
                      className={`replace-part-row ${selectedPartId === part.partId ? 'selected-row' : ''}`}
                      onClick={() => setSelectedPartId(part.partId)}
                    >
                      <TableCell padding="checkbox">
                        <Radio
                          checked={selectedPartId === part.partId}
                          value={part.partId}
                          onChange={() => setSelectedPartId(part.partId)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{part.orderNumber}</TableCell>
                      <TableCell>{part.layer}</TableCell>
                      <TableCell>
                        <strong>{part.mark}</strong>
                      </TableCell>
                      <TableCell>{part.name}</TableCell>
                      <TableCell>{part.type}</TableCell>
                      <TableCell align="right">{part.weight ? `${part.weight.toFixed(1)}` : '-'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <TableContainer component={Paper} className="replace-part-table-container" elevation={0}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>Mark</TableCell>
                  <TableCell>Part Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Weight (lbs)</TableCell>
                  <TableCell>Status / Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUnplacedParts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                      No unplaced steel parts available.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUnplacedParts.map((part) => (
                    <TableRow
                      key={part.partId}
                      className={`replace-part-row ${selectedPartId === part.partId ? 'selected-row' : ''}`}
                      onClick={() => setSelectedPartId(part.partId)}
                    >
                      <TableCell padding="checkbox">
                        <Radio
                          checked={selectedPartId === part.partId}
                          value={part.partId}
                          onChange={() => setSelectedPartId(part.partId)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <strong>{part.mark}</strong>
                      </TableCell>
                      <TableCell>{part.name}</TableCell>
                      <TableCell>{part.type}</TableCell>
                      <TableCell align="right">{part.weight ? `${part.weight.toFixed(1)}` : '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={part.reason ? `Excluded: ${part.reason}` : 'Unplaced'}
                          size="small"
                          color={part.reason ? 'warning' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
          </>
        )}
      </DialogContent>

      <DialogActions className="replace-part-actions">
        {hasReplacementOptions ? (
          <>
            <Button onClick={onClose} color="inherit">
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              variant="contained"
              color="primary"
              disabled={!selectedPartId}
              startIcon={<SwapHorizIcon />}
            >
              Replace
            </Button>
          </>
        ) : (
          <Button onClick={onClose} variant="contained" color="primary">
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
